import type { ChangeEvent } from 'react'
import { REFERENCE_TYPE_OPTIONS } from './types'
import type {
  ReferenceUploadFormErrors,
  ReferenceUploadFormValues,
} from './types'

interface Props {
  values: ReferenceUploadFormValues
  errors: ReferenceUploadFormErrors
  disabled?: boolean
  onChange: (
    field: keyof ReferenceUploadFormValues,
    value: string,
  ) => void
}

/**
 * Metadata fields for the Reference Library upload form.
 *
 * Design notes (Step 8.0):
 *   - No entity/group fields — reference documents are reusable and live
 *     outside the case/client/patient hierarchy.
 *   - ``reference_type`` is a closed list of canonical categories.
 *   - All other fields are optional but typed: ``reference_year`` is a
 *     four-digit number; ``reference_domain`` / ``reference_source`` are
 *     free strings sanitized server-side.
 *   - ``tenant_id`` is sourced from the authenticated organization — never
 *     editable here (cookie-only auth model).
 */
export function ReferenceMetadataForm({
  values,
  errors,
  disabled,
  onChange,
}: Props) {
  const handleInput =
    (field: keyof ReferenceUploadFormValues) =>
    (event: ChangeEvent<HTMLInputElement>) =>
      onChange(field, event.target.value)

  const handleSelect =
    (field: keyof ReferenceUploadFormValues) =>
    (event: ChangeEvent<HTMLSelectElement>) =>
      onChange(field, event.target.value)

  return (
    <div className="doc-form">
      <div className="doc-form-grid">
        <Field
          label="Título"
          value={values.title}
          error={errors.title}
          placeholder="Protocolo de Dor Lombar"
          helper="Opcional. Aparece no card; o nome do arquivo é usado como fallback."
          disabled={disabled}
          onChange={handleInput('title')}
          full
        />
      </div>

      <div className="doc-form-grid">
        <label className="doc-field">
          <span className="doc-field-label">
            Tipo de referência
            <span className="doc-field-optional">opcional</span>
          </span>
          <select
            value={values.referenceType}
            onChange={handleSelect('referenceType')}
            disabled={disabled}
            className={`doc-field-input${errors.referenceType ? ' has-error' : ''}`}
          >
            <option value="">— selecione —</option>
            {REFERENCE_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {!errors.referenceType ? (
            <span className="doc-field-helper">
              Protocolo, lei, guia, política, manual, SOP ou base interna.
            </span>
          ) : (
            <span className="doc-field-error">{errors.referenceType}</span>
          )}
        </label>

        <Field
          label="Domínio"
          value={values.referenceDomain}
          error={errors.referenceDomain}
          placeholder="medical"
          helper="Opcional. Ex.: medical, legal, operations, compliance."
          disabled={disabled}
          onChange={handleInput('referenceDomain')}
        />
      </div>

      <div className="doc-form-grid">
        <Field
          label="Ano"
          value={values.referenceYear}
          error={errors.referenceYear}
          placeholder="2026"
          helper="Opcional. Quatro dígitos entre 1900 e 2100."
          disabled={disabled}
          onChange={handleInput('referenceYear')}
          inputMode="numeric"
        />
        <Field
          label="Fonte"
          value={values.referenceSource}
          error={errors.referenceSource}
          placeholder="ANVISA"
          helper="Opcional. Origem, autor ou órgão emissor."
          disabled={disabled}
          onChange={handleInput('referenceSource')}
        />
      </div>

      <div className="doc-form-grid">
        <Field
          label="ID do documento"
          value={values.documentId}
          error={errors.documentId}
          placeholder="protocolo_dor_lombar_2026"
          helper="Gerado automaticamente a partir do nome do arquivo. Edite para sobrescrever."
          disabled={disabled}
          onChange={handleInput('documentId')}
          full
          mono
        />
      </div>
    </div>
  )
}

interface FieldProps {
  label: string
  value: string
  onChange: (event: ChangeEvent<HTMLInputElement>) => void
  placeholder?: string
  required?: boolean
  helper?: string
  error?: string
  disabled?: boolean
  full?: boolean
  mono?: boolean
  inputMode?: 'text' | 'numeric'
}

function Field({
  label,
  value,
  onChange,
  placeholder,
  required,
  helper,
  error,
  disabled,
  full,
  mono,
  inputMode,
}: FieldProps) {
  return (
    <label className={`doc-field${full ? ' doc-field-full' : ''}`}>
      <span className="doc-field-label">
        {label}
        {required ? (
          <em className="doc-field-required" aria-hidden="true">
            *
          </em>
        ) : (
          <span className="doc-field-optional">opcional</span>
        )}
      </span>
      <input
        type="text"
        inputMode={inputMode}
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        disabled={disabled}
        className={`doc-field-input${mono ? ' mono' : ''}${error ? ' has-error' : ''}`}
        spellCheck={false}
        autoComplete="off"
      />
      {helper && !error ? (
        <span className="doc-field-helper">{helper}</span>
      ) : null}
      {error ? <span className="doc-field-error">{error}</span> : null}
    </label>
  )
}
